import { useDeviceStatus } from '@/composeables/devicestatus/devicestatus';
import { featureIcons } from '../components/Menu/featureicons';
import { MenuItem, type MenuStatusType } from '@/objects/MenuItem';
import { storeToRefs } from 'pinia';
import { reactive } from 'vue';
import { useCommonStoreRef, useEventCommonStoreRef, useEventStore, useEventStoreRef, useRaceControlStoreRef } from '@/stores';
import { StackedIcon } from '@/fontawesome';
import WellKnown from '@/objects/generated/static/WellKnown';

export const menu = reactive<Array<MenuItem>>([
    new MenuItem({
        route: '/home',
        icon: 'fa-home',
        visibleIn: 'ALL',
        subItems: [],
        translationKey: 'menu.home',
        name: 'Home',
        eventNeeded: false,
        prependEventUID: false,
        shortcutKey: 'CTRL+ALT+H',
    }),

    new MenuItem({
        route: '/dashboard',
        icon: 'fa-game-board',
        visibleIn: 'ALL',
        subItems: [],
        translationKey: 'dashboard.dashboardpage',
        name: 'Dashboard',
        eventNeeded: true,
        prependEventUID: true,
        shortcutKey: 'CTRL+ALT+D',
    }),

    new MenuItem({
        groupStart: true,
        route: '',
        icon: '',
        visibleIn: 'ALL',
        translationKey: 'menu.before_event',
        name: 'Before event',
        eventNeeded: true,
        prependEventUID: true,
        subItems: [
            new MenuItem({
                route: '/eventinfo',
                icon: featureIcons.EVENTS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.Info',
                name: 'Info',
                eventNeeded: true,
                prependEventUID: true,
            }),

            // The contestant page
            new MenuItem({
                route: '/contestants/general',
                icon: featureIcons.CONTESTANT,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.contestants',
                name: 'Contestants',
                eventNeeded: true,
                prependEventUID: true,
                shortcutKey: 'CTRL+ALT+P',
            }),
            new MenuItem({
                route: '/races/general',
                icon: featureIcons.RACES,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'race.races',
                name: 'Races',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/traininglanes/groups',
                icon: featureIcons.TRAININGLANES,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.traininglanes',
                name: 'Training lanes',
                eventNeeded: true,
                prependEventUID: true,
                functionalityTypes: ['Shooting'],
            }),
            new MenuItem({
                route: '/categories/general',
                icon: featureIcons.CATEGORY,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'category.categories',
                name: 'Categories',
                eventNeeded: true,
                prependEventUID: true,
                shortcutKey: 'CTRL+ALT+C',
            }),

            new MenuItem({
                route: '/groups/general',
                icon: featureIcons.GROUPS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'group.groups',
                name: 'Groups',
                eventNeeded: true,
                prependEventUID: true,
            }),

            new MenuItem({
                route: '/racesetup/general',
                icon: featureIcons.RACESETUP,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'racesetup.racesetup',
                name: 'racesetup',
                eventNeeded: true,
                prependEventUID: true,
            }),

            new MenuItem({
                route: '/bibs/general',
                icon: featureIcons.BIBS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.bibs',
                name: 'Bibs',
                eventNeeded: true,
                prependEventUID: true,
                shortcutKey: 'CTRL+ALT+B',
            }),

            // The contestant page
            new MenuItem({
                route: '/eventgraphics/texts',
                icon: featureIcons.EVENTGRAPHICS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.eventgraphics',
                name: 'Eventgraphics',
                eventNeeded: true,
                prependEventUID: true,
                shortcutKey: 'CTRL+ALT+G',
            }),

            new MenuItem({
                route: '/ibu/general',
                icon: featureIcons.FEDERATION,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'ibu.ibu',
                name: 'IBU',
                roles: [],
                functionalityTypes: ['Shooting'],
                eventNeeded: true,
                prependEventUID: true,
            }),

            new MenuItem({
                route: '/fis/event',
                icon: featureIcons.FEDERATION,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'fis.fis',
                name: 'FIS',
                roles: [],
                eventNeeded: true,
                prependEventUID: true,
                visibleCallback: () => {
                    // We show fis only if federation is one with ski
                    const { Event } = useEventCommonStoreRef();
                    return [WellKnown.FEDERATION_SKIFORBUNDET].includes(Event.value?.FederationID ?? 0);
                },
            }),

            /** FileArchive menu items */
            new MenuItem({
                route: '/eventfilearchive/general',
                icon: featureIcons.FILEARCHIVE,
                visibleIn: 'ALL',
                translationKey: 'menu.filearchive',
                name: 'Filearchive',
                eventNeeded: true,
                prependEventUID: true,
                subItems: [],
            }),
        ],
    }),
    new MenuItem({
        groupStart: true,
        route: '',
        icon: '',
        visibleIn: 'ALL',
        translationKey: 'menu.during_event',
        name: 'During event',
        eventNeeded: true,
        prependEventUID: true,
        subItems: [
            new MenuItem({
                route: '/quickinput/timerecorder',
                icon: 'fa-pen-field',
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.quickinput',
                name: 'QuickInput',
                eventNeeded: true,
                prependEventUID: true,
                shortcutKey: 'CTRL+ALT+Q',
            }),

            new MenuItem({
                route: '/unknowntags/general',
                icon: featureIcons.UNKNOWNTAGS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.unknowntags',
                name: 'UnknownTags',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/matrix/general',
                icon: featureIcons.MATRIX,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.matrix',
                name: 'Matrix',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/sprint/general',
                icon: featureIcons.SPRINT,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.sprint',
                name: 'Sprint',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/tracking/map',
                icon: featureIcons.TRACKING,
                status: (): MenuStatusType => {
                    // We need this setting to know if we should listen for global statuses. and then set the online state on the menu icon
                    const { Event } = useEventCommonStoreRef();
                    const now = new Date();
                    const isValidEvent = (Event.value?.EventStart ?? now) <= now && (Event.value?.EventEnd ?? now) >= now;
                    return isValidEvent && Event.value?.ResultSetup?.LiveTracking === true ? 'ONLINE' : 'HIDDEN';
                },
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.tracking',
                name: 'Tracking',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/eventdevicestatus/general',
                icon: featureIcons.DEVICESTATUS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.devicestatus',
                name: 'EventDeviceStatus',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/gpioreadings/general',
                icon: featureIcons.READINGS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.gpioreadings',
                name: 'GPIOReadings',
                eventNeeded: true,
                prependEventUID: true,
            }),
            new MenuItem({
                route: '/production/screens',
                icon: featureIcons.PRODUCTION,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.production',
                name: 'Production',
                eventNeeded: true,
                prependEventUID: true,
            }),

            /** Speaker menu items */
            new MenuItem({
                groupStart: false,
                route: '/cis/general',
                icon: 'fa-bullhorn',
                visibleIn: 'ALL',
                translationKey: 'menu.cis',
                name: 'Speaker',
                eventNeeded: true,
                badgetext: 'BETA',
                roles: ['superadmin'], // This will also affect all subitems
                prependEventUID: true,
                visibleCallback: () => {
                    return true;
                },
                subItems: [],
            }),
        ],
    }),

    /**
     * Race control menu item
     */
    new MenuItem({
        groupStart: false,
        route: '/racecontrol/general',
        icon: 'fa-tower-control',
        visibleIn: 'ALL',
        subItems: [],
        translationKey: 'eventinfo.racecontrol',
        name: 'Racecontrol',
        eventNeeded: true,
        prependEventUID: true,
        status: (): MenuStatusType => {
            // We need this setting to know if we should listen for global statuses. and then set the online state on the menu icon
            const { IsRunning } = useRaceControlStoreRef();
            return IsRunning.value ? 'ONLINE' : 'HIDDEN';
        },
    }),

    new MenuItem({
        groupStart: false,
        route: '/reports/general',
        icon: 'fa-print',
        visibleIn: 'ALL',
        subItems: [],
        translationKey: 'reports.reports',
        name: 'Reports',
        eventNeeded: true,
        prependEventUID: true,
    }),

    /** Series menu items */
    new MenuItem({
        groupStart: true,
        route: '/series/general',
        icon: '',
        visibleIn: 'ALL',
        translationKey: 'menu.series',
        name: 'Series',
        eventNeeded: false,
        prependEventUID: false,
        visibleCallback: () => {
            // Check if the user has access to the series
            const { Organizers } = useCommonStoreRef();
            return Organizers.value?.length > 0;
        },
        subItems: [
            new MenuItem({
                route: '/series/series',
                icon: featureIcons.SERIES,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.series',
                name: 'Series',
                eventNeeded: false,
                prependEventUID: false,
            }),
        ],
    }),

    /**
     * Superadmin menu items
     */
    new MenuItem({
        groupStart: true,
        route: '',
        icon: '',
        visibleIn: 'ALL',
        translationKey: 'menu.superadmin',
        name: 'Super',
        eventNeeded: false,
        prependEventUID: false,
        roles: ['superadmin'], // This will also affect all subitems
        subItems: [
            new MenuItem({
                route: '/devicestatus/status',
                icon: featureIcons.DEVICESTATUS,
                status: (): MenuStatusType => {
                    // We need this setting to know if we should listen for global statuses. and then set the online state on the menu icon
                    const { ListenForGlobalStatuses } = storeToRefs(useDeviceStatus());
                    return ListenForGlobalStatuses.value === true ? 'ONLINE' : 'HIDDEN';
                },
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.devicestatus',
                name: 'DeviceStatus',
                eventNeeded: false,
                prependEventUID: false,
            }),

            new MenuItem({
                route: '/trackdevices/devices',
                icon: featureIcons.TRACKINGDEVICE,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.trackdevices',
                name: 'TrackDevices',
                eventNeeded: false,
                prependEventUID: false,
            }),

            new MenuItem({
                route: '/approvecommunication',
                icon: featureIcons.COMMUNICATION,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.approvecommunication',
                name: 'CommunicationToApprove',
                eventNeeded: false,
                prependEventUID: false,
            }),

            new MenuItem({
                route: '/users',
                icon: featureIcons.USERS,
                visibleIn: 'ALL',
                subItems: [],
                translationKey: 'menu.users',
                name: 'users',
                eventNeeded: false,
                prependEventUID: false,
            }),

            /** Licence check view*/
            new MenuItem({
                route: '/licences/general',
                icon: featureIcons.LICENSES,
                visibleIn: 'ALL',
                translationKey: 'menu.licences',
                name: 'Licences',
                eventNeeded: false,
                badgetext: 'BETA',
                roles: ['superadmin'], // This will also affect all subitems
                prependEventUID: false,
                subItems: [],
            }),

            /** FileArchive menu items
             * I dont think we need this one. This will be an extremely large list if we have many events.
             *
             */
            // new MenuItem({
            //     groupStart: true,
            //     route: '/filearchive',
            //     icon: featureIcons.FILEARCHIVE,
            //     visibleIn: 'ALL',
            //     translationKey: 'menu.filearchive',
            //     name: 'Filearchive',
            //     eventNeeded: false,
            //     prependEventUID: false,
            //     subItems: [],
            // }),
        ],
    }),

    /**
     * General menu items
     */
    // new MenuItem({
    //     groupStart: true,
    //     route: '/notifications',
    //     icon: 'fa-bell',
    //     visibleIn: 'ALL',
    //     subItems: [],
    //     translationKey: 'menu.notifications',
    //     name: 'Notifications',
    //     eventNeeded: false,
    //     prependEventUID: false,
    //     badgecount: 8,
    // }),

    // new MenuItem({
    //     route: '/support',
    //     icon: 'fa-comments-question-check',
    //     visibleIn: 'ALL',
    //     subItems: [],
    //     translationKey: 'menu.support',
    //     name: 'Support',
    //     eventNeeded: false,
    //     prependEventUID: false,
    //     status: (): MenuStatusType => {
    //         return 'UNAVAILABLE';
    //     },
    // }),

    new MenuItem({
        groupStart: true,
        route: '/about',
        icon: 'fa-circle-question',
        visibleIn: 'ALL',
        subItems: [],
        translationKey: 'menu.about',
        name: 'About',
        eventNeeded: false,
        prependEventUID: false,
    }),
    ...(import.meta.env.MODE !== 'development'
        ? []
        : [
              new MenuItem({
                  groupStart: true,
                  route: '/devdemo',
                  icon: 'fa-circle-question',
                  visibleIn: 'ALL',
                  subItems: [],
                  translationKey: 'menu.devdemo',
                  name: 'DevDemo',
                  eventNeeded: false,
                  prependEventUID: false,
              }),
          ]),
]);
